import { useContext } from 'react'
import { TypeCategoryWithoutUnresolvableLinksResponse } from 'bb-contentful-types'

import { FlexGrid, Feedback } from '@telus-uds/components-web'
import { articleConsumer } from '../../../provider/ArticleProvider'
import Articles from '../Articles'
import SkeletonViewArticles from '../SkeletonViewArticles'
import { PreviewContainerWhiteBackground, PreviewContainerGrayBackground, BottomNavigationContainer } from '../styles'
import { useFetchArticles } from '@/hooks/useFetchArticles'
import { flattenData } from '../utils/flattenData'
import { findArticleIds } from '../utils/findArticleIds'
import Pagination from '../../Pagination'
import STATIC_CONTENTS from '../../../utils/staticContent'
import { filterArticleTags } from '../../../utils/filterArticleTags'

import type { Language } from '@/siteBuilder/locale'
import type { BlogUrl } from 'src/blogBuilder/utils/blogUrl'

export type Props = {
  blogs: TypeCategoryWithoutUnresolvableLinksResponse[]
  lang: Language
  fullBlogUrls: BlogUrl
  theme: 'blog' | 'story'
}

const PaginationPreview = ({ blogs, lang, fullBlogUrls, theme }: Props) => {
  const { selectedCategory, allArticles, currentPage, setCurrentPage, selectedTags, articlesPerPage } =
    useContext(articleConsumer)
  const PreviewContainer = theme === 'story' ? PreviewContainerWhiteBackground : PreviewContainerGrayBackground

  const filteredArticles = filterArticleTags(allArticles, selectedTags)
  const paginatedArticles = filteredArticles.slice(currentPage * articlesPerPage, (currentPage + 1) * articlesPerPage)
  const articleIds = findArticleIds(blogs, paginatedArticles, selectedCategory)
  const { data, isLoading, isError } = useFetchArticles(articleIds, lang)

  if (isError || data?.sys?.type === 'Error') {
    return (
      <Feedback validation="error" variant={{ icon: true }} title="Sorry">
        Something went wrong...
      </Feedback>
    )
  }

  return (
    <>
      <PreviewContainer data-testid="bb-story-preview">
        <FlexGrid>
          <FlexGrid.Row>
            {isLoading && <SkeletonViewArticles count={articlesPerPage} theme={theme} />}
            {data && (
              <Articles
                theme={theme}
                fullBlogUrls={fullBlogUrls}
                listOfArticles={flattenData([data])}
                lang={lang}
                selectedCategory={selectedCategory}
                displayCategory={selectedCategory === STATIC_CONTENTS.ALL[lang]}
              />
            )}
          </FlexGrid.Row>

          <FlexGrid.Row horizontalAlign="center">
            <FlexGrid.Col xs={12}>
              <BottomNavigationContainer>
                <Pagination
                  lang={lang}
                  displayCount={articlesPerPage}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  numOfArticles={filteredArticles.length}
                />
              </BottomNavigationContainer>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </PreviewContainer>
    </>
  )
}

export default PaginationPreview
